import { useStrings } from "@1js/localization-components";
import {
  Button,
  Menu,
  MenuList,
  MenuPopover,
  MenuTrigger,
  type MenuProps,
} from "@fluentui/react-components";
import { useControllableValue } from "@fluentui/react-hooks";
import * as React from "react";
import { MenuFilterPlaceholderValueLabel } from "../PromptFilter.strings";
import { useStyles } from "../PromptFilter.styles";
import { PromptFiltersSkeleton } from "../PromptFilterSkeleton";
import { usePromptFilterLocalizations } from "../usePromptFilterStrings";
import { groupFilters } from "../utils";
import type { FilterProps } from "./Filter.types";
import { RenderMenuOptions } from "./RenderMenuOptions";

export const MenuFilter: React.FC<FilterProps> = ({
  appearance,
  tab,
  filter,
  isLoading,
  selectedValues,
  defaultSelectedValues,
  onChange,
  onOpenChanged,
  isMetaOsV2,
  isRockSteadyUxEnabled,
  disabledFilters,
  selectedFilterValue,
}) => {
  const styles = useStyles(appearance)();
  const [, setValues] = useControllableValue(
    selectedValues,
    defaultSelectedValues,
    onChange
  );
  const localizations = useStrings({
    MenuFilterPlaceholderValueLabel,
  });

  const {
    facetKeyToLabel,
    facetValueToLabel,
    selectedValueToLabel,
    groupValueToLabel,
  } = usePromptFilterLocalizations();
  const filterLabel = facetKeyToLabel(
    filter.key,
    isMetaOsV2 || isRockSteadyUxEnabled
  );
  const placeholderValue = localizations.MenuFilterPlaceholderValueLabel();
  const filterByGroup = groupFilters(filter);
  const [checkedValues, setCheckedValues] = React.useState<
    Record<string, string[]>
  >({});
  const onChangeChecked: MenuProps["onCheckedValueChange"] = (
    _e,
    { name, checkedItems }: { name: string; checkedItems: string[] }
  ) => {
    setCheckedValues((prevCheckedValues) => ({
      ...prevCheckedValues,
      [name]: checkedItems,
    }));

    const allCheckedItems = Object.values({
      ...checkedValues,
      [name]: checkedItems,
    }).flat();

    setValues(allCheckedItems);
  };
  const displayedValueText =
    (disabledFilters?.[filter.key] && selectedFilterValue) || // Show selectedFilterValue only if the filter is disabled
    (Object.values(checkedValues).flat().length > 0
      ? Object.values(checkedValues)
          .flat()
          .map((value: string) => {
            const filterOption = filter.options.find(
              (option) => option.value === value
            );
            if (filterOption?.displayLabel) {
              return filterOption.displayLabel;
            }

            return selectedValueToLabel(value);
          })
          .join(", ")
      : "");

  const handleOnOpenChanged = React.useCallback(
    (
      _e:
        | React.MouseEvent<HTMLElement>
        | React.KeyboardEvent<HTMLElement>
        | React.TouchEvent<HTMLElement>
        | MouseEvent
        | KeyboardEvent
        | TouchEvent,
      data: { open: boolean }
    ) => {
      onOpenChanged && onOpenChanged(data.open, [filter]);
    },
    [filter, onOpenChanged]
  );

  if (filter.options.length < 1) {
    return null;
  }

  return (
    <React.Fragment key={`${tab}-${filter.key}`}>
      {isLoading ? (
        <PromptFiltersSkeleton />
      ) : (
        <Menu
          positioning={{ autoSize: true }}
          onOpenChange={handleOnOpenChanged}
        >
          <MenuTrigger disableButtonEnhancement>
            <Button
              shape="circular"
              disabled={disabledFilters?.[filter.key] || filter.disabled}
              data-testid={`${filter.key}-menu`}
            >
              <span className={styles.truncatedText}>
                {filterLabel}:{" "}
                {displayedValueText || (
                  <span className={styles.truncatedText}>
                    {placeholderValue}
                  </span>
                )}
              </span>
            </Button>
          </MenuTrigger>

          <MenuPopover>
            <MenuList
              checkedValues={checkedValues}
              onCheckedValueChange={onChangeChecked}
            >
              {Object.entries(filterByGroup).map(
                ([groupKey, filterOptions]) => (
                  <React.Fragment key={groupKey}>
                    <RenderMenuOptions
                      groupKey={groupKey}
                      groupLabel={groupValueToLabel(groupKey)}
                      filterOptions={filterOptions}
                      selectedValues={checkedValues}
                      valueToLabel={facetValueToLabel}
                      appearance={appearance}
                    />
                  </React.Fragment>
                )
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    </React.Fragment>
  );
};
